export interface BirSaleDetail {
  clientName: string;
  amount: number;
  fingerprint: string;
  source: string;
  tin_number: string;
  business_name: string;
  business_address: string;
}

export interface BirLogEntry extends BirSaleDetail {
  sequenceNumber: number;
  transactionHash: string;
  timestamp: number;
  synced: boolean;
}

async function computeHash(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function logBirTransaction(sale: BirSaleDetail): Promise<BirLogEntry> {
  const lastCounterStr = localStorage.getItem('cf_bir_sequence_counter') || '0';
  const nextCounter = parseInt(lastCounterStr, 10) + 1;
  localStorage.setItem('cf_bir_sequence_counter', nextCounter.toString());

  const lastHash = localStorage.getItem('cf_bir_last_hash') || 'BIR-ROOT-HASH-2026';

  const timestamp = Date.now();
  const blockContent = `${nextCounter}|${sale.clientName}|${sale.amount}|${sale.fingerprint}|${sale.source}|${sale.tin_number}|${sale.business_name}|${sale.business_address}|${timestamp}|${lastHash}`;
  const transactionHash = await computeHash(blockContent);

  localStorage.setItem('cf_bir_last_hash', transactionHash);

  const logEntry: BirLogEntry = {
    ...sale,
    sequenceNumber: nextCounter,
    transactionHash,
    timestamp,
    synced: false
  };

  const pendingRaw = localStorage.getItem('pending_bir_sync');
  const pendingList: BirLogEntry[] = pendingRaw ? JSON.parse(pendingRaw) : [];
  pendingList.push(logEntry);
  localStorage.setItem('pending_bir_sync', JSON.stringify(pendingList));

  const historyRaw = localStorage.getItem('cf_bir_log_history');
  const historyList: BirLogEntry[] = historyRaw ? JSON.parse(historyRaw) : [];
  historyList.push(logEntry);
  localStorage.setItem('cf_bir_log_history', JSON.stringify(historyList));

  return logEntry;
}

export async function syncPendingBirTransactions(
  pushCallback: (entry: BirLogEntry) => Promise<boolean>
): Promise<number> {
  const pendingRaw = localStorage.getItem('pending_bir_sync');
  if (!pendingRaw) return 0;

  const pendingList: BirLogEntry[] = JSON.parse(pendingRaw);
  if (pendingList.length === 0) return 0;

  if (!navigator.onLine) {
    return 0;
  }

  const remaining: BirLogEntry[] = [];
  let syncedCount = 0;

  for (const entry of pendingList) {
    try {
      const success = await pushCallback(entry);
      if (success) {
        entry.synced = true;
        syncedCount++;
        const historyRaw = localStorage.getItem('cf_bir_log_history');
        if (historyRaw) {
          const historyList: BirLogEntry[] = JSON.parse(historyRaw);
          const idx = historyList.findIndex(h => h.transactionHash === entry.transactionHash);
          if (idx !== -1 && historyList[idx] !== undefined) {
            const histEntry = historyList[idx];
            if (histEntry) {
              histEntry.synced = true;
            }
            localStorage.setItem('cf_bir_log_history', JSON.stringify(historyList));
          }
        }
      } else {
        remaining.push(entry);
      }
    } catch (err) {
      remaining.push(entry);
    }
  }

  localStorage.setItem('pending_bir_sync', JSON.stringify(remaining));
  return syncedCount;
}

export function exportBirLogsToCsv(): void {
  const historyRaw = localStorage.getItem('cf_bir_log_history');
  const historyList: BirLogEntry[] = historyRaw ? JSON.parse(historyRaw) : [];

  const headers = [
    'Sequence',
    'Timestamp',
    'Client Name',
    'Amount (PHP)',
    'Device Fingerprint',
    'Source',
    'TIN Number',
    'Business Name',
    'Business Address',
    'Tamper-Proof Hash',
    'Synced Status'
  ];
  const rows = historyList.map(entry => [
    entry.sequenceNumber,
    new Date(entry.timestamp).toISOString(),
    `"${entry.clientName.replace(/"/g, '""')}"`,
    entry.amount.toFixed(2),
    entry.fingerprint,
    entry.source,
    entry.tin_number,
    `"${entry.business_name.replace(/"/g, '""')}"`,
    `"${entry.business_address.replace(/"/g, '""')}"`,
    entry.transactionHash,
    entry.synced ? 'SYNCED' : 'PENDING'
  ]);

  const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bir_sales_journal_${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
